import { reduced, resetInitial } from "../gsap";
import { Scroll } from "../scroll";

/**
 * Navigation Module
 *
 * Handles mobile navigation overlay with GSAP animations and smooth scroll-to functionality.
 * Features:
 * - Mobile: Fullscreen overlay with clip-path animation
 * - Desktop: Fixed vertical sidebar
 * - Smooth scroll-to sections using Lenis
 * - Close on: ESC key, outside click, scroll-to button click
 *
 * Usage:
 * <header data-module="nav">
 *   <button data-nav-toggle>Menu</button>
 *   <nav data-nav-overlay>
 *     <div data-nav-items>
 *       <button data-scrollto="#section">Section</button>
 *     </div>
 *   </nav>
 * </header>
 */
export class Nav {
  #navOverlay;
  #navItems;
  #navToggle;
  #scrollToButtons;
  #isOpen = false;
  #mediaQuery;
  #themeObservers = [];

  constructor(element) {
    this.element = element;

    // Get DOM elements
    this.#navOverlay = element.querySelector("[data-nav-overlay]");
    this.#navItems = element.querySelector("[data-nav-items]");
    this.#navToggle = element.querySelector("[data-nav-toggle]");
    this.#scrollToButtons = element.querySelectorAll("[data-scrollto]");

    if (!this.#navOverlay || !this.#navToggle) {
      console.warn("Nav: Required elements not found");
      return;
    }

    // Setup media query for mobile/desktop detection
    this.#mediaQuery = window.matchMedia("(min-width: 1040px)");

    // Bind methods
    this.handleToggle = this.handleToggle.bind(this);
    this.handleScrollTo = this.handleScrollTo.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);

    // Initialize
    this.#init();
    
    // Setup theme detection for desktop nav
    this.#setupThemeDetection();
    
    // Make element visible after preloader (respects data-start="hidden")
    resetInitial(element);
  }

  #init() {
    // CSS handles the default closed state on mobile
    // No need to add is-closed class
    
    // Add event listeners
    this.#navToggle.addEventListener("click", this.handleToggle);

    this.#scrollToButtons.forEach((button) => {
      button.addEventListener("click", this.handleScrollTo);
    });

    // Listen for resize to close mobile nav on desktop
    this.#mediaQuery.addEventListener("change", this.handleResize);
  }

  #setupThemeDetection() {
    // Find all sections with dark backgrounds
    const darkSections = document.querySelectorAll('[data-nav-theme="dark"]');
    if (darkSections.length === 0) return;

    const isDesktop = this.#mediaQuery.matches;

    // Get elements to check based on viewport
    const elementsToCheck = isDesktop
      ? Array.from(this.#navItems.children) // Desktop: nav items
      : [this.#navToggle]; // Mobile: nav toggle button

    // Check each element against dark sections on scroll
    const checkElementTheme = (element) => {
      const elementRect = element.getBoundingClientRect();
      const elementCenter = elementRect.top + elementRect.height / 2;

      // Check if the center of the element is over any dark section
      let isOverDark = false;

      darkSections.forEach((section) => {
        const sectionRect = section.getBoundingClientRect();

        // Check if element center is within the dark section's vertical bounds
        if (elementCenter >= sectionRect.top && elementCenter <= sectionRect.bottom) {
          isOverDark = true;
        }
      });

      // Update class and cursor attribute based on position
      if (isOverDark) {
        element.classList.add("is-over-dark");
        element.setAttribute("data-cursor", "-inverse");
      } else {
        element.classList.remove("is-over-dark");
        element.removeAttribute("data-cursor");
      }
    };

    // Check all elements
    const checkAllElements = () => {
      elementsToCheck.forEach((element) => {
        checkElementTheme(element);
      });
    };

    // Initial check
    checkAllElements();

    // Subscribe to scroll events
    const unsubscribe = Scroll.subscribe(() => {
      checkAllElements();
    });

    // Store cleanup function
    this.#themeObservers.push({ unsubscribe, elements: elementsToCheck });
  }

  #cleanupThemeDetection() {
    // Unsubscribe from scroll events and remove classes
    this.#themeObservers.forEach(({ unsubscribe, elements }) => {
      if (unsubscribe) unsubscribe();
      elements.forEach((element) => {
        element.classList.remove("is-over-dark");
      });
    });
    this.#themeObservers = [];
  }

  handleToggle(e) {
    e.preventDefault();

    if (this.#isOpen) {
      this.closeMobileNav();
    } else {
      this.openMobileNav();
    }
  }

  openMobileNav() {
    if (this.#isOpen) return;

    this.#isOpen = true;
    this.#navToggle.setAttribute("aria-expanded", "true");
    
    // Store original label and switch to alternative label if available
    this.#navToggle.setAttribute("data-og-label", this.#navToggle.textContent);
    const altLabel = this.#navToggle.getAttribute('data-alt-label');
    if (altLabel) {
      this.#navToggle.textContent = altLabel;
    }
    
    // Add open state class - CSS handles all animations
    this.#navOverlay.classList.add("is-open");

    // Add keydown listener for ESC key
    document.addEventListener("keydown", this.handleKeydown);

    // Stop scrolling when nav is open
    Scroll.stop();
  }

  closeMobileNav() {
    if (!this.#isOpen) return;

    this.#isOpen = false;
    this.#navToggle.setAttribute("aria-expanded", "false");
    
    // Restore original label
    const ogLabel = this.#navToggle.getAttribute('data-og-label');
    if (ogLabel) {
      this.#navToggle.textContent = ogLabel;
    }

    // Remove open state class - CSS handles all animations
    this.#navOverlay.classList.remove("is-open");

    // Remove event listeners
    document.removeEventListener("keydown", this.handleKeydown);

    // Re-enable scrolling
    Scroll.start();
  }

  handleScrollTo(e) {
    e.preventDefault();

    const target = e.currentTarget.dataset.scrollto;
    if (!target) return;

    // Close mobile nav first if open
    if (this.#isOpen && !this.#mediaQuery.matches) {
      this.closeMobileNav();

      // Wait for close animation to finish before scrolling
      const delay = reduced ? 0 : 600;
      setTimeout(() => {
        this.#scrollToSection(target);
      }, delay);
    } else {
      this.#scrollToSection(target);
    }
  }

  #scrollToSection(target) {
    // Remove # if present
    const targetId = target.replace("#", "");
    const targetElement = document.getElementById(targetId);

    if (!targetElement) {
      console.warn(`Nav: Target element "${targetId}" not found`);
      return;
    }

    // Use Lenis scrollTo method
    Scroll.scrollTo(targetElement, {
      offset: 0,
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
  }

  handleKeydown(e) {
    // Close on ESC key
    if (e.key === "Escape" && this.#isOpen) {
      this.closeMobileNav();
    }
  }

  handleResize() {
    const isDesktop = this.#mediaQuery.matches;
    
    if (isDesktop) {
      // Switching to desktop
      
      // Close mobile nav if it's open
      if (this.#isOpen) {
        this.#isOpen = false;
        this.#navToggle.setAttribute("aria-expanded", "false");
        this.#navOverlay.classList.remove("is-open");
        document.removeEventListener("keydown", this.handleKeydown);
        Scroll.start();
      }
      
      // Always cleanup and re-setup theme detection when switching to desktop
      this.#cleanupThemeDetection();
      this.#setupThemeDetection();
    } else {
      // Switching to mobile - cleanup theme detection
      this.#cleanupThemeDetection();
    }
  }

  transitionOut() {
    // Clean up event listeners
    this.#navToggle?.removeEventListener("click", this.handleToggle);

    this.#scrollToButtons?.forEach((button) => {
      button.removeEventListener("click", this.handleScrollTo);
    });

    document.removeEventListener("keydown", this.handleKeydown);
    this.#mediaQuery?.removeEventListener("change", this.handleResize);

    // Cleanup theme detection
    this.#cleanupThemeDetection();

    // Re-enable scrolling if it was stopped
    if (this.#isOpen) {
      Scroll.start();
    }
  }
}
